import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useResource, formatDateTime } from "../lib/hooks";
import { InitialsAvatar } from "../components/InitialsAvatar";

interface ChatMessage {
    id: string;
    channel_id: string;
    body: string;
    author_name: string | null;
    created_at: string;
}

/**
 * Portal Messages (C10 phase 4): a provider talks to the TrustCare team from
 * inside their portal. Same DM machinery the staff Chat uses — membership-
 * scoped RLS means a partner can only ever read conversations they are in,
 * and their directory is limited server-side to TrustCare admin/managers.
 */
export const PartnerMessages = () => {
    const dms = useResource<Array<{ id: string; partner_name: string }>>("/chat-dms");
    const unread = useResource<Array<{ channel_id: string; unread: number }>>("/chat-unread");
    const directory = useResource<Array<{ id: string; full_name: string }>>("/chat-directory");

    const [channelId, setChannelId] = useState<string | null>(null);
    const [draft, setDraft] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const endRef = useRef<HTMLDivElement>(null);

    const messages = useResource<ChatMessage[]>(
        channelId ? `/chat-messages?channel_id=${channelId}` : null);

    useEffect(() => {
        endRef.current?.scrollIntoView({ block: "end" });
        if (channelId) {
            void api.post(`/chat-channels/${channelId}/read`, {}).then(() => unread.reload());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channelId, messages.data?.length]);

    const unreadFor = (id: string) =>
        (unread.data ?? []).find((u) => u.channel_id === id)?.unread ?? 0;

    const openWith = async (userId: string) => {
        setError(null);
        try {
            const dm = await api.post<{ id: string }>("/chat-dm", { user_id: userId });
            dms.reload();
            setChannelId(dm.id);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not open the conversation");
        }
    };

    const send = async () => {
        if (!channelId || !draft.trim()) return;
        setBusy(true);
        setError(null);
        try {
            await api.post("/chat-messages", { channel_id: channelId, body: draft.trim() });
            setDraft("");
            messages.reload();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not send the message");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: "0.9rem", alignItems: "start" }}>
            <div className="card">
                <div className="card-head"><h2>Conversations</h2></div>
                <div className="card-body" style={{ display: "grid", gap: "0.3rem" }}>
                    {(dms.data ?? []).map((dm) => (
                        <button key={dm.id} className={`chip-toggle ${channelId === dm.id ? "on" : ""}`}
                            style={{ display: "flex", justifyContent: "space-between", width: "100%" }}
                            onClick={() => setChannelId(dm.id)}>
                            <span>{dm.partner_name}</span>
                            {unreadFor(dm.id) > 0 && <span className="chip block">{unreadFor(dm.id)}</span>}
                        </button>
                    ))}
                    {(dms.data ?? []).length === 0 && (
                        <p style={{ fontSize: "0.78rem", color: "var(--muted)" }}>
                            No conversations yet — start one below.
                        </p>
                    )}
                    <div style={{ borderTop: "1px solid var(--line)", marginTop: "0.5rem", paddingTop: "0.5rem" }}>
                        <div style={{ fontSize: "0.72rem", color: "var(--muted)", textTransform: "uppercase",
                            letterSpacing: "0.06em", marginBottom: "0.35rem" }}>
                            Message the TrustCare team
                        </div>
                        {(directory.data ?? []).map((person) => (
                            <button key={person.id} className="icon-btn" style={{ display: "block", width: "100%", textAlign: "left" }}
                                onClick={() => void openWith(person.id)}>
                                + {person.full_name}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <div className="card" style={{ minHeight: 420, display: "flex", flexDirection: "column" }}>
                {error && <div className="error-banner">{error}</div>}
                {!channelId ? (
                    <div className="card-body empty" style={{ margin: "auto" }}>
                        Pick a conversation, or message the TrustCare team — we reply here and
                        you will see an unread badge when we do.
                    </div>
                ) : (
                    <>
                        <div className="card-body" style={{ flex: 1, overflowY: "auto", display: "grid",
                            gap: "0.6rem", alignContent: "start", maxHeight: 460 }}>
                            {(messages.data ?? []).map((message) => (
                                <div key={message.id} style={{ display: "flex", gap: "0.55rem" }}>
                                    <InitialsAvatar name={message.author_name} size={26} />
                                    <div>
                                        <div style={{ fontSize: "0.72rem", color: "var(--muted)" }}>
                                            {message.author_name ?? "Unknown"} · {formatDateTime(message.created_at)}
                                        </div>
                                        <div style={{ fontSize: "0.86rem", whiteSpace: "pre-wrap" }}>{message.body}</div>
                                    </div>
                                </div>
                            ))}
                            {(messages.data ?? []).length === 0 && (
                                <div className="empty">No messages yet — say hello.</div>
                            )}
                            <div ref={endRef} />
                        </div>
                        <div style={{ display: "flex", gap: "0.5rem", padding: "0.7rem",
                            borderTop: "1px solid var(--line)" }}>
                            <input style={{ flex: 1 }} value={draft} placeholder="Write a message…"
                                onChange={(e) => setDraft(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(); } }} />
                            <button className="btn" disabled={busy || !draft.trim()} onClick={() => void send()}>
                                Send
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
